import { all, call, put, take, takeLatest } from 'redux-saga/effects'
import * as api from '../services/api'
import { getEntityById } from '../modules/nexusFuncs'
import { CATEGORY, KIND } from '../modules/constants'
import { COUNTRY } from '../modules/Countries'

const initialState = {
  favourites: null,
  isFetching: false,
}

export function reducer(state = initialState, action) {

  switch (action.type) {

    case 'fav.get': {
      return {
        ...state,
        isFetching: true,
      }
    }

    case 'fav.set': {
      // console.log('fav set action:', action)
      return {
        ...state,
        favourites: action.favourites,
        isFetching: false,
      }
    }

    default: {
      return state
    }
  }
}

function* handleAdd(props) {
  let { entity, entities, features } = props
  // If we came from search officer, entity has limited info; eg no countryOfResidence for person
  if (entity?.kind === KIND.SEARCH.RESULTS.OFFICER) {
    // So, begin by getting an existing entity if we have one
    const existingEntity = getEntityById(entities, [entity._id])
    // If the saved entity exists, use this for our favourite
    if (existingEntity)
      entity = existingEntity
    else {
      // Otherwise get the appointments CH and use this for our favourite
      // TODO: Ideally, we should save this entity!
      const newEntities = yield call(api.entities.getAppointmentEntities, entity._id, !!features.MaxLseData)

      if (newEntities.items.length)
        entity = newEntities.items[0]
    }
  }
  entity = getAbridgedEntity(entity)
  const favourites = yield call(api.favourites.addFavourite, entity)
  yield put(actions.set(favourites))
}

// Returns an array of country codes corresponding to the countries in the countryOfResidence string
// Eg: "Scotland, United Kingdom" >  [2826, 826]
const GetCountryCodesArray = countryOfResidence =>
  countryOfResidence.split(", ").map(c => Object.keys(COUNTRY).find(key => COUNTRY[key].country === c)).filter(element => element !== undefined)

// Returns an array of country codes corresponding to the nationalities in the nationality string
// Eg. "British, American" > [826, 840]
const GetNationalityCodesArray = nationality =>
  nationality.split(", ").map(c => Object.keys(COUNTRY).find(key => COUNTRY[key].nationality === c)).filter(element => element !== undefined)

function getAbridgedEntity(entity) {
  // Redefine and return entity using only the elements used in backend AddFavouriteAsync
  const { _id, category, companyProps, ids, name, personInfo } = entity
  const commonProps = { _id, category, ids, name }

  return entity.category === CATEGORY.COMPANY
    ? {
      address: companyProps?.companyProfile?.registeredOfficeAddress ?? entity.address,
      companyNumber: companyProps?.number ?? entity.ids[0],
      companyStatus: companyProps?.status ?? entity.companyStatus,
      ...commonProps,
    }
    : {
      personInfo: {
        age: personInfo.age,
        dateOfBirth: personInfo.dateOfBirth,
        // lse people use 'countryOfResidence' and nationality strings (eg "Scotland, United Kingdom"),
        // rather than 'countries' and nationalities arrays (eg  [2826, 826]) used by nexus,
        // so we need to convert them using GetCountryCodesArray() and GetNationalityCodesArray()
        countries: personInfo.countries ?? GetCountryCodesArray(personInfo.countryOfResidence),
        nationalities: personInfo.nationalities ?? GetNationalityCodesArray(personInfo.nationality),
      },
      ...commonProps,
    }
}

function* handleDelete({ category, id }) {
  const favourites = yield call(api.favourites.deleteFavourite, category, id)
  yield put(actions.set(favourites))
}

function* handleGet() {
  const favourites = yield call(api.favourites.getFavourites)
  yield put(actions.set(favourites))
}

// fav.add and fav.delete each end by replacing the *entire* favourites list with whatever their
// own addFavourite/deleteFavourite response contained (see favouritesApi.js) - not a delta. Two
// of these in flight at once (e.g. a user clicking two favourite stars in quick succession, or
// this app's own favourites.spec.js round-tripping several rows) race on which response's
// snapshot gets applied last, and the loser's change is silently dropped from the client's view
// even though it may still be persisted server-side. Serializing every add/delete through one
// queue (rather than takeEvery, which runs each concurrently) guarantees they're applied in the
// order they were dispatched, so no response can ever be overwritten by a stale one.
function* watchFavouriteMutations() {
  while (true) {
    const action = yield take(['fav.add', 'fav.delete'])
    if (action.type === 'fav.add')
      yield call(handleAdd, action)
    else
      yield call(handleDelete, action)
  }
}

export const actions = {
  add: props => ({ type: 'fav.add', ...props }),
  delete: (category, id) => ({ type: 'fav.delete', category, id }),
  get: () => ({ type: 'fav.get' }),
  set: favourites => ({ favourites, type: 'fav.set' }),
}

export function* sagas() {
  yield all([
    watchFavouriteMutations(),
    takeLatest('fav.get', handleGet),
  ]);
}
